/**
 * Gelir Vergisi Hesaplama Engine
 * 2026 Türkiye — 332 No'lu Gelir Vergisi Genel Tebliği (31.12.2025 Resmi Gazete)
 * Ücret gelirleri dilimleri esas alınmıştır.
 */

// ─── 2026 Sabitleri ───────────────────────────────────────────
export const YILI = 2026;
export const BRUT_ASGARI_UCRET = 33_030;

// SGK Oranları (işçi)
export const SGK_ISCI_ORAN = 0.14;
export const ISSIZLIK_ISCI_ORAN = 0.01;
export const DAMGA_VERGISI_ORAN = 0.00759;
export const SGK_TAVAN_AYLIK = 297_270;

// ─── 2026 Gelir Vergisi Dilimleri (Ücret Gelirleri) ──────────
export interface VergiDilimi {
  altSinir: number;
  ustSinir: number | null; // null = sınırsız
  oran: number;
  kumulatifVergi: number; // alt sınıra kadar birikmiş vergi
  label: string;
}

export const VERGI_DILIMLERI_2026: VergiDilimi[] = [
  {
    altSinir: 0,
    ustSinir: 190_000,
    oran: 0.15,
    kumulatifVergi: 0,
    label: "0 – 190.000 ₺",
  },
  {
    altSinir: 190_000,
    ustSinir: 400_000,
    oran: 0.2,
    kumulatifVergi: 28_500,
    label: "190.001 – 400.000 ₺",
  },
  {
    altSinir: 400_000,
    ustSinir: 1_500_000,
    oran: 0.27,
    kumulatifVergi: 70_500,
    label: "400.001 – 1.500.000 ₺",
  },
  {
    altSinir: 1_500_000,
    ustSinir: 5_300_000,
    oran: 0.35,
    kumulatifVergi: 367_500,
    label: "1.500.001 – 5.300.000 ₺",
  },
  {
    altSinir: 5_300_000,
    ustSinir: null,
    oran: 0.4,
    kumulatifVergi: 1_697_500,
    label: "5.300.001 ₺ ve üzeri",
  },
];

// ─── Tipler ───────────────────────────────────────────────────
export type HesaplamaTipi = "brut_ucret" | "yillik_matrah";

export interface GelirVergisiGirdi {
  hesaplamaTipi: HesaplamaTipi;
  brutAylikUcret?: number;
  yillikMatrah?: number;
  ayIndex?: number; // 0-11 arası, hangi ay (kümülatif hesaplama için)
}

export interface AylikDetay {
  ay: number; // 1-12
  ayAdi: string;
  aylikBrut: number;
  sgkKesinti: number;
  issizlikKesinti: number;
  aylikMatrah: number;
  kumulatifMatrah: number;
  aylikGelirVergisi: number;
  kumulatifGelirVergisi: number;
  damgaVergisi: number;
  netUcret: number;
  efektifOran: number; // o ay için efektif vergi oranı
  icindeBulunanDilim: string;
}

export interface GelirVergisiSonuc {
  // Girdi
  hesaplamaTipi: HesaplamaTipi;
  brutAylikUcret: number;

  // Yıllık toplamlar
  yillikBrutUcret: number;
  yillikSgkKesinti: number;
  yillikIssizlikKesinti: number;
  yillikMatrah: number;
  yillikGelirVergisi: number;
  yillikDamgaVergisi: number;
  yillikToplamKesinti: number;
  yillikNetUcret: number;
  ortalamaEfektifOran: number;

  // Aylık detaylar
  aylikDetaylar: AylikDetay[];

  // Dilim dağılımı
  dilimDagilimi: {
    dilim: VergiDilimi;
    matrah: number;
    vergi: number;
  }[];
}

// ─── Yardımcı ─────────────────────────────────────────────────
const AY_ADLARI = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

export function formatTL(value: number): string {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatOran(value: number): string {
  return `%${(value * 100).toFixed(2)}`;
}

// ─── Kümülatif Vergi Hesaplama ────────────────────────────────
function hesaplaKumulatifVergi(kumulatifMatrah: number): number {
  if (kumulatifMatrah <= 0) return 0;

  for (const dilim of VERGI_DILIMLERI_2026) {
    const ustSinir = dilim.ustSinir ?? Infinity;
    if (kumulatifMatrah <= ustSinir) {
      return (
        dilim.kumulatifVergi + (kumulatifMatrah - dilim.altSinir) * dilim.oran
      );
    }
  }

  // Son dilim üstü (güvenlik)
  const sonDilim = VERGI_DILIMLERI_2026[VERGI_DILIMLERI_2026.length - 1];
  return (
    sonDilim.kumulatifVergi +
    (kumulatifMatrah - sonDilim.altSinir) * sonDilim.oran
  );
}

function buldunDilim(kumulatifMatrah: number): string {
  for (const dilim of VERGI_DILIMLERI_2026) {
    const ustSinir = dilim.ustSinir ?? Infinity;
    if (kumulatifMatrah <= ustSinir) {
      return `${dilim.label} (${formatOran(dilim.oran)})`;
    }
  }
  const sonDilim = VERGI_DILIMLERI_2026[VERGI_DILIMLERI_2026.length - 1];
  return `${sonDilim.label} (${formatOran(sonDilim.oran)})`;
}

// ─── Ana Hesaplama ────────────────────────────────────────────
export function hesaplaGelirVergisi(
  girdi: GelirVergisiGirdi
): GelirVergisiSonuc {
  const brutAylik = Math.max(0, girdi.brutAylikUcret || 0);

  const aylikDetaylar: AylikDetay[] = [];
  let kumulatifMatrah = 0;
  let kumulatifGelirVergisi = 0;
  let yillikSgk = 0;
  let yillikIssizlik = 0;
  let yillikDamga = 0;
  let yillikNet = 0;

  for (let ay = 0; ay < 12; ay++) {
    // SGK kesintileri (tavan kontrolü)
    const sgkMatrahi = Math.min(brutAylik, SGK_TAVAN_AYLIK);
    const sgkKesinti = sgkMatrahi * SGK_ISCI_ORAN;
    const issizlikKesinti = sgkMatrahi * ISSIZLIK_ISCI_ORAN;

    // Gelir vergisi matrahı
    const aylikMatrah = brutAylik - sgkKesinti - issizlikKesinti;
    const yeniKumulatifMatrah = kumulatifMatrah + aylikMatrah;

    // Kümülatif vergi farkı = bu ayın vergisi
    const yeniKumulatifVergi = hesaplaKumulatifVergi(yeniKumulatifMatrah);
    const aylikGelirVergisi = yeniKumulatifVergi - kumulatifGelirVergisi;

    // Damga vergisi
    const damgaVergisi = brutAylik * DAMGA_VERGISI_ORAN;

    // Net ücret
    const netUcret =
      brutAylik -
      sgkKesinti -
      issizlikKesinti -
      aylikGelirVergisi -
      damgaVergisi;

    // Efektif oran
    const efektifOran = brutAylik > 0 ? aylikGelirVergisi / brutAylik : 0;

    aylikDetaylar.push({
      ay: ay + 1,
      ayAdi: AY_ADLARI[ay],
      aylikBrut: brutAylik,
      sgkKesinti,
      issizlikKesinti,
      aylikMatrah,
      kumulatifMatrah: yeniKumulatifMatrah,
      aylikGelirVergisi,
      kumulatifGelirVergisi: yeniKumulatifVergi,
      damgaVergisi,
      netUcret,
      efektifOran,
      icindeBulunanDilim: buldunDilim(yeniKumulatifMatrah),
    });

    kumulatifMatrah = yeniKumulatifMatrah;
    kumulatifGelirVergisi = yeniKumulatifVergi;
    yillikSgk += sgkKesinti;
    yillikIssizlik += issizlikKesinti;
    yillikDamga += damgaVergisi;
    yillikNet += netUcret;
  }

  // Dilim dağılımı
  const dilimDagilimi = VERGI_DILIMLERI_2026.map((dilim) => {
    const ustSinir = dilim.ustSinir ?? Infinity;
    let matrah = 0;
    if (kumulatifMatrah > dilim.altSinir) {
      matrah = Math.min(kumulatifMatrah, ustSinir) - dilim.altSinir;
    }
    return {
      dilim,
      matrah: Math.max(0, matrah),
      vergi: Math.max(0, matrah) * dilim.oran,
    };
  }).filter((d) => d.matrah > 0);

  const yillikBrut = brutAylik * 12;
  const yillikToplamKesinti =
    yillikSgk + yillikIssizlik + kumulatifGelirVergisi + yillikDamga;

  return {
    hesaplamaTipi: girdi.hesaplamaTipi || "brut_ucret",
    brutAylikUcret: brutAylik,
    yillikBrutUcret: yillikBrut,
    yillikSgkKesinti: yillikSgk,
    yillikIssizlikKesinti: yillikIssizlik,
    yillikMatrah: kumulatifMatrah,
    yillikGelirVergisi: kumulatifGelirVergisi,
    yillikDamgaVergisi: yillikDamga,
    yillikToplamKesinti,
    yillikNetUcret: yillikNet,
    ortalamaEfektifOran:
      yillikBrut > 0 ? kumulatifGelirVergisi / yillikBrut : 0,
    aylikDetaylar,
    dilimDagilimi,
  };
}
